import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Modal from "../../components/modal/modal"; // Укажи свой правильный путь к модалке

const EditNews = () => {
  const { id } = useParams(); // Получаем ID новости из URL (`/EditNews/:id`)
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true); // Состояние загрузки старых данных
  const [modalMessage, setModalMessage] = useState(null);

  // 1. При загрузке страницы получаем текущие данные этой новости
  useEffect(() => {
    const fetchSingleNews = async () => {
      try {
        const res = await fetch(`/api/get_single_news.php?id=${id}`);
        const data = await res.json();

        if (data.success && data.news) {
          setTitle(data.news.title);
          setDescription(data.news.description);
          if (data.news.image) {
            // Ставим старую картинку как дефолтное превью
            setImagePreview(`/api/uploads/${data.news.image}`);
          }
        } else {
          setModalMessage({
            title: t("error"),
            text: data.message || "Публикация не найдена",
            success: false,
            ok: t("ok"),
            onClose: () => navigate(-1),
          });
        }
      } catch (err) {
        console.error("Ошибка при получении новости:", err);
      } finally {
        setFetching(false);
      }
    };

    fetchSingleNews();
  }, [id, navigate, t]);

  // 2. Если пользователь выбрал НОВЫЙ файл, генерируем его локальное превью
  useEffect(() => {
    if (!imageFile) return;
    const objectUrl = URL.createObjectURL(imageFile);
    setImagePreview(objectUrl);

    // Очищаем память при размонтировании
    return () => URL.revokeObjectURL(objectUrl);
  }, [imageFile]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // При редактировании файл картинки не обязателен (можно оставить старый)
    if (!title || !description) {
      setModalMessage({
        title: t("error"),
        text: t("allfields"),
        success: false,
        ok: t("ok"),
      });
      return;
    }

    const formData = new FormData();
    formData.append("id", id); // ID новости, чтобы бэкенд знал, что обновлять
    formData.append("title", title);
    formData.append("description", description);
    
    // Отправляем файл только если пользователь выбрал новую картинку
    if (imageFile) {
      formData.append("image", imageFile);
    }

    try {
      setLoading(true);
      const res = await fetch("/api/update_company_news.php", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.success) {
        setModalMessage({
          title: t("success"),
          text: t("data_saved") || "Изменения успешно сохранены!",
          success: true,
          ok: t("ok"),
          onClose: () => navigate(-1), // Возвращаемся назад в список новостей
        });
      } else {
        setModalMessage({
          title: t("error"),
          text: data.message || t("error_updating_news"),
          success: false,
          ok: t("ok"),
        });
      }
    } catch (err) {
      console.error("Error:", err);
      setModalMessage({
        title: t("error"),
        text: t("server_error"),
        success: false,
        ok: t("ok"),
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="w-full flex justify-center items-center min-h-[400px]">
        <p className="text-white text-xl animate-pulse">{t("Loading")}...</p>
      </div>
    );
  }

  return (
    <section className="w-full opacity-0 animate-[showtitle_1s_forwards] flex justify-center p-3 items-center flex-col">
      {/* Модалка для алертов */}
      {modalMessage && (
        <Modal
          title={modalMessage.title}
          text={modalMessage.text}
          success={modalMessage.success}
          ok={modalMessage.ok}
          onClose={() => {
            if (modalMessage.onClose) modalMessage.onClose();
            setModalMessage(null);
          }}
        />
      )}

      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-2xl my-12 rounded-2xl w-[700px] max-md:w-full overflow-hidden flex flex-col"
      >
        <h1 className="bg-[#006674] p-3 text-center text-white text-3xl max-sm:text-xl font-semibold">
          {t("editnews") || "Редактировать публикацию"}
        </h1>
        <div className="flex w-full flex-col justify-start items-center gap-3">
          
          {/* Обложка с возможностью замены */}
          <label className="flex flex-col items-center justify-center h-[300px] max-sm:h-[200px] w-full bg-gray-400 cursor-pointer overflow-hidden relative group">
            {imagePreview ? (
              <>
                <img
                  src={imagePreview}
                  alt="preview"
                  className="h-full w-full object-cover"
                />
                {/* Эффект при наведении, подсказывающий, что фото можно сменить */}
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <span className="text-white font-semibold uppercase text-sm">
                    {t("change_photo") || "Изменить фото"}
                  </span>
                </div>
              </>
            ) : (
              <>
                <span className="font-semibold uppercase">{t("photo")}</span>
                <span className="font-semibold uppercase">{t("onlyonephoto")}</span>
              </>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                if (e.target.files[0]) setImageFile(e.target.files[0]);
              }}
              className="hidden"
            />
          </label>

          <div className="p-6 w-full">
            {/* Инпут Заголовка */}
            <label className="flex w-full flex-col gap-1 mb-4">
              <span className="font-semibold uppercase text-[#006674] text-sm">
                {t("title")}
              </span>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="border rounded-lg w-full transition-all p-2 outline-none focus:ring-2 ring-[#006674]"
              />
            </label>

            {/* Текстовое поле Описания */}
            <label className="flex flex-col w-full gap-1 mb-4">
              <span className="font-semibold uppercase text-[#006674] text-sm">
                {t("information")}
              </span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="border w-full rounded-lg p-2 min-h-[150px] transition-all outline-none focus:ring-2 ring-[#006674]"
              ></textarea>
            </label>

            {/* Кнопка отправки */}
            <button
              type="submit"
              disabled={loading}
              className="bg-[#006674] w-full text-white py-2 px-4 rounded-lg hover:bg-[#008ca6] transition-all duration-200 disabled:opacity-60 font-semibold text-lg"
            >
              {loading ? t("Saving...") || "Сохранение..." : t("save") || "Сохранить изменения"}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
};

export default EditNews;